import { fetchProviderByReference } from "../services/providerService";
import { fetchPatientByReference } from "../services/patientService";
import { fetchSpecimenByReference } from "../services/specimenService";
import { fetchObservationByReference } from "../services/observationService";
import { fetchDiagnosticReportByReference } from "../services/diagnosticReportService";
import { fetchDocumentReferenceByReference } from "../services/docReferenceService";
import { fetchEncounterByReference } from "../services/encounterService";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchConditionByReference } from "../services/conditionService";
import { fetchCarePlanByReference } from "../services/carePlanService";
import { fetchCareTeamByReference } from "../services/careTeamService";
import { fetchMediaByReference } from "../services/mediaService";
import { fetchServiceRequestByReference } from "../services/serviceRequestService";
import { fetchQuestionnaireResponseByReference } from "../services/questionnaireResponseService";

const { updateHydration, addMessage, acknowledgeNewMessage } = require('../slices/messageSlice');

/** Extracts Binary resource URLs from a DocumentReference. */
export function getBinaryResourcesFromDocumentReference(documentRef) {
    if (!documentRef || !Array.isArray(documentRef.content)) return [];
    return documentRef.content
        .map(item => item.attachment?.url)
        .filter(url => typeof url === 'string' && url.includes('Binary/'));
}

/** Wraps a FHIR resource into a displayable message object. */
export function createWrappedMessage({
    sourceType,
    fileType,
    resourceId,
    title,
    resource,
    attachment,
    index
}) {
    return {
        type: sourceType,
        fileType,
        resourceId,
        title,
        date: resource?.date || null,
        sourceDescription: resource?.description || null,
        index,
        ...(sourceType === "DocumentReference" || sourceType === "Media"
            ? { resource }
            : { attachment }),
    };
}

export function toBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            // Remove the prefix "data:<mime-type>;base64,"
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
        };
        reader.onerror = error => reject(error);
    });
}


/** Builds a Communication payload for sending. */
export async function buildCommunicationPayload({
    contextProvider,
    recipients,
    selectedPatient,
    encounter,
    topic,
    priority,
    category,
    about,
    reason,
    anchorReference,
    replyToCommId,
    message,
    selectedDocuments
}) {
    const senderRef = `Practitioner/${contextProvider?.id}`;
    const now = new Date().toISOString();

    // Start with initial about array (deduplicated)
    const aboutSet = new Set(about?.map(a => a.reference) || []);

    const payload = {
        resourceType: "Communication",
        status: "completed",
        sender: { reference: senderRef },
        recipient: recipients.map(r => ({ reference: `${r.resourceType}/${r.id}` })),
        subject: { reference: `Patient/${selectedPatient?.id}` },
        encounter: { reference: `Encounter/${encounter?.id}` },
        topic: {
            coding: [{
                system: "http://example.com/custom-topic-system",
                code: "progress-update",
                display: topic
            }]
        },
        priority: priority || "routine",
        category: [{
            coding: [{
                system: "http://terminology.hl7.org/CodeSystem/communication-category",
                code: category,
                display: category.charAt(0).toUpperCase() + category.slice(1)
            }]
        }],
        about: about ? [...about] : [],
        reasonCode: reason ? [{
            coding: [{
                system: "http://snomed.info/sct",
                code: reason,
                display: reason
            }]
        }] : [],
        ...(anchorReference && { partOf: [{ reference: anchorReference }] }),
        ...(replyToCommId && { inResponseTo: [{ reference: `Communication/${replyToCommId}` }] }),
        payload: [{ contentString: message }],
        sent: now,
        extension: []
    };

    // Helper function to add about reference if not already present
    const addAboutReference = (ref) => {
        if (!aboutSet.has(ref)) {
            payload.about.push({ reference: ref });
            aboutSet.add(ref);
        }
    };

    // Process selected documents
    for (const doc of selectedDocuments) {
        console.log("Selected Documents", selectedDocuments);


        if (doc.resource) {
            addAboutReference(`${doc.resource.resourceType}/${doc.resource.id}`);

        } else if (doc.attachment) {
            try {
                const base64Data = await toBase64(doc.attachment);
                payload.payload.push({
                    contentAttachment: {
                        contentType: doc.attachment.type || "application/octet-stream",
                        title: doc.title,
                        data: base64Data
                    }
                });
            } catch (err) {
                console.error(`❌ Failed to encode attachment ${doc.title}:`, err);
            }
        }
    }

    // Add read-receipt extension if message is sent to others
    const mockMessage = { sender: { reference: senderRef } };
    const isSelf = isSelfMessage(mockMessage, contextProvider?.id);

    if (!isSelf) {
        payload.extension.push({
            url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
            extension: [
                { url: "user", valueReference: { reference: senderRef } },
                { url: "readAt", valueDateTime: now }
            ]
        });
    }

    return payload;
}

export const hydrateMessage = async ({
    msg,
    selfId,
    store,
    fhirClient
}) => {
    if (msg.meta?.hydrated) {
        console.log("🔁 Already hydrated:", msg.id);
        return msg;
    }
    //console.log("💧 Hydrating message:", msg.id);
    const senderRef = msg.sender?.reference;
    const senderId = senderRef?.split("/")?.pop();
    const isSelf = senderId === selfId;
    const senderName = await resolveName(senderRef, store, fhirClient);
    const content = msg.payload?.find(p => p.contentString)?.contentString || "[No content]";
    const attachments = msg.payload?.filter(p => p.contentAttachment)?.map(p => p.contentAttachment) || [];

    const readReceipts = msg.extension?.filter(ext =>
        ext.url === "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt"
    ) || [];

    const readDetails = await Promise.all(
        readReceipts
            .filter(ext => {
                const readerRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const readerId = readerRef?.split("/")?.pop();
                return readerId !== senderId;
            })
            .map(async ext => {
                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const readerName = await resolveName(userRef, store, fhirClient);
                const readAt = ext.extension?.find(e => e.url === "readAt")?.valueDateTime;
                return readerName && readAt ? { reader: readerName, readAt } : null;
            })
    );

    const status = isSelf ? (msg.meta?.isPending ? "⏳ ...Sending" : "✅ Sent") : null;

    const enriched = {
        id: msg.id,
        sent: msg.sent,
        sender: senderName || senderRef || "Unknown",
        isSelf,
        content,
        attachments,
        status,
        readDetails: readDetails.filter(Boolean)
    };

    return {
        ...msg,
        meta: {
            ...(msg.meta || {}),
            hydrated: true,
            enrichedData: enriched,
            hydratedAt: new Date().toISOString()
        }
    };
};


//TODO: use this function instead of hydrateMessages in fetchNewMessages.js and see what happens.
/**
 * Hydrates and dispatches an array of Communication messages directly without searching for redux messages.
 *
 * @param {object[]} messages - Array of raw Communication resources
 * @param {object} contextProvider - Practitioner context
 * @param {object} store - Redux store
 * @param {object} fhirClient - FHIR client instance
 * @param {boolean} isMounted - Component mount flag
 * @param {function} dispatch - Redux dispatch
 * @returns {Promise<object[]>} - Array of hydrated messages
 */
export const hydrateMessageArray = async ({
    messages,
    contextProvider,
    store,
    fhirClient,
    isMounted,
    dispatch
}) => {
    try {
        if (!Array.isArray(messages) || messages.length === 0) {
            console.log("⚠️ No messages provided for hydration");
            return [];
        }

        console.log(`💧 Hydrating ${messages.length} messages from array...`);
        const selfId = contextProvider?.id?.split("/")?.pop();
        const hydratedMessages = [];

        for (const msg of messages) {
            if (!isMounted) {
                console.log("⛔ Aborting hydration — component unmounted.");
                break;
            }

            const hydrated = await hydrateMessage({
                msg,
                selfId,
                store,
                fhirClient
            });

            if (hydrated) {
                hydratedMessages.push(hydrated);
                dispatch(updateHydration({
                    id: hydrated.id,
                    enriched: hydrated.meta.enrichedData
                }));
            }
        }

        return hydratedMessages;
    } catch (err) {
        console.error("🚨 Error hydrating message array:", err);
        throw err;
    }
};


export const hydrateMessages = async ({
    messageIds,
    byId,
    contextProvider,
    store,
    fhirClient,
    isMounted,
    dispatch
}) => {
    try {
        console.log("💧 Starting individual hydration for messages:", messageIds);

        const selfId = contextProvider?.id?.split("/")?.pop();
        const rawMessages = messageIds.map(id => byId?.[id]).filter(Boolean);
        const hydratedMessages = [];

        console.log(`💧 Hydrating ${rawMessages.length} messages individually...`);

        for (const msg of rawMessages) {
            console.log(`💧 Hydrating message ${msg.id}...`);
            if (!isMounted) {
                console.log("⛔ Aborting hydration — component unmounted.");
                break;
            }

            const hydrated = await hydrateMessage({
                msg,
                selfId,
                store,
                fhirClient
            });

            console.log(`💧 Hydrated message ${msg.id}:`, hydrated);

            if (hydrated) {
                hydratedMessages.push(hydrated);
                dispatch(updateHydration({
                    id: hydrated.id,
                    enriched: hydrated.meta.enrichedData
                }));
            }
        }

        return hydratedMessages;
    } catch (err) {
        console.error("🚨 Error hydrating messages individually:", err);
        throw err;
    }
};

// messageUtils.js

/**
 * Checks if a Communication message is hydrated in Redux.
 * @param {Object} message - The Communication object (must have an `id`).
 * @param {Object} byId - The Redux slice containing messages keyed by ID.
 * @returns {boolean} - True if hydrated, false otherwise.
 */
export function isHydrated(message, byId) {
    if (!message?.id || !byId?.[message.id]) return false;
    return !!byId[message.id]?.meta?.hydrated;
};


export function hasBeenReadByCurrentUser(comm, userId) {
    if (!comm || !userId) return false;

    const normalizedUserId = userId.split("/")?.pop(); // e.g., "1234" from "Practitioner/1234"

    // ✅ If the sender is the current user, it's already read
    const senderRef = comm.sender?.reference;
    const normalizedSenderId = senderRef?.split("/")?.pop();
    if (normalizedSenderId === normalizedUserId) return true;

    // ✅ Otherwise, check for read receipts
    const receipts = comm.extension?.filter(
        (ext) => ext.url === "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt"
    ) || [];

    return receipts.some((receipt) => {
        const userRef = receipt.extension?.find(e => e.url === "user")?.valueReference?.reference;
        const readAt = receipt.extension?.find(e => e.url === "readAt")?.valueDateTime;
        const normalizedRefId = userRef?.split("/")?.pop();

        return normalizedUserId === normalizedRefId && !!readAt;
    });
}



export function promoteMessageFromNewToMain(message, store) {
    store.dispatch(addMessage(message)); // adds to messages.byId
    store.dispatch(acknowledgeNewMessage(message.id)); // removes from newMessages
};

//for updating read receipts on the right reducer.
export function dispatchMessageUpdate(message, store) {
    const state = store.getState();
    const inNewMessages = !!state.messages.newMessages.byId[message.id];

    if (inNewMessages) {
        store.dispatch(acknowledgeNewMessage(message.id));
    }

    store.dispatch(addMessage(message));
};

export function isSelfMessage(message, providerId) {
    if (!message || !message.sender || !providerId) return false;
    const senderRef = message.sender.reference || "";
    return senderRef === `Practitioner/${providerId}`;
};


/**
 * Extract attachment and referenced resources from a FHIR Communication resource.
 * @param {Object} message - FHIR Communication resource.
 * @returns {Array} - Array of attachment objects:
 *                    { type: "attachment" | "reference", url, title, contentType }
 */
/**
 * Extracts attachments from a FHIR Communication resource.
 * Returns both payload attachments and about references.
 */
export function getMessageAttachments(message) {
    //console.log("Getting message attachments...........");
    if (!message) return [];

    const attachments = [];

    const aboutRefs = new Set(
        Array.isArray(message.about)
            ? message.about.map(ref => ref.reference)
            : []
    );

    // Process payload
    if (Array.isArray(message.payload)) {
        message.payload.forEach((entry, idx) => {
            if (entry.contentAttachment) {
                //console.log(entry);
                // Extract the resource ID from the URL
                const urlId = entry.contentAttachment.url?.split('/').pop();
                if (
                    aboutRefs.has(`Observation/${urlId}`) || aboutRefs.has(`DiagnosticReport/${urlId}`)
                ) {
                    return;
                }
                attachments.push({
                    type: 'attachment',
                    ...entry.contentAttachment
                });
            } else if (entry.contentReference?.reference) {
                attachments.push({
                    type: 'reference',
                    url: entry.contentReference.reference
                });
            }
        });
    }

    // Add about references
    if (Array.isArray(message.about)) {
        message.about.forEach((ref, idx) => {
            if (ref.reference) {
                attachments.push({
                    type: 'reference',
                    url: ref.reference
                });
            }
        });
    }

    //console.log("getMessageAttachments: final attachments", attachments);
    return attachments;
}


/**
 * Builds a FHIR AuditEvent agent object from a contextProvider.
 * @param {Object} contextProvider - The Practitioner resource or null.
 * @returns {Object} FHIR AuditEvent agent object.
 */
export function buildAgentFromContextProvider(contextProvider) {
    if (contextProvider) {
        return {
            who: {
                reference: `Practitioner/${contextProvider.id}`,
                display:
                    contextProvider.name?.[0]?.text ||
                    `${contextProvider.name?.[0]?.given?.join(" ")} ${contextProvider.name?.[0]?.family}` ||
                    "Unknown"
            },
            requestor: true
        };
    }
    return {
        who: { reference: "Practitioner/unknown", display: "Unknown" },
        requestor: true
    };
}

/**
 * Builds a FHIR AuditEvent entity object from a contextPatient.
 * @param {Object} contextPatient - The Patient resource or null.
 * @returns {Object} FHIR AuditEvent entity object.
 */
export function buildEntityFromContextPatient(contextPatient) {
    if (contextPatient) {
        return {
            what: {
                reference: `Patient/${contextPatient.id}`,
                display:
                    contextPatient.name?.[0]?.text ||
                    `${contextPatient.name?.[0]?.given?.join(" ")} ${contextPatient.name?.[0]?.family}` ||
                    "Unknown"
            },
            role: { code: "1", display: "Patient" }
        };
    }
    return {
        what: { reference: "Patient/unknown", display: "Unknown" },
        role: { code: "1", display: "Patient" }
    };
}

/**
 * Validates FHIR references for communications
 * Ensures patients are patients, practitioners are practitioners, etc.
 * @param {Array} messages - Array of Communication resources
 * @param {object} fhirClient - FHIR client instance
 * @param {function} getState - Redux getState
 * @param {function} dispatch - Redux dispatch
 * @returns {Promise<Array>} - Array of validated messages
 */
export async function validateCommunications(messages, fhirClient, store) {
    const validated = [];
    for (const comm of messages) {
        //console.log(`Validating communication ${comm.id}...`, comm);
        let isValid = true;

        // 1. Validate patient (subject)
        const subjectRef = comm.subject?.reference;
        if (subjectRef?.startsWith("Patient/")) {
            const patientId = subjectRef.split("/")[1];
            let patient = patientId ? store.getState().patients?.[patientId] : null;
            if (!patient) {
                patient = await fetchPatientByReference(subjectRef, fhirClient, store);
            }
            if (!patient || patient.resourceType !== "Patient") {
                console.log(`Invalid patient reference for comm ${comm.id}:`, subjectRef);
                isValid = false;
            }
        }

        // 2. Validate sender
        const senderRef = comm.sender?.reference;
        if (senderRef?.startsWith("Practitioner/")) {
            const senderId = senderRef.split("/")[1];
            let sender = senderId ? store.getState().providers?.[senderId] : null;
            if (!sender) {
                sender = await fetchProviderByReference(senderRef, fhirClient, store);
            }
            if (!sender || sender.resourceType !== "Practitioner") {
                console.log(`Invalid practitioner reference for comm ${comm.id}:`, senderRef);
                isValid = false;
            }
        } else if (senderRef?.startsWith("Patient/")) {
            const patientId = senderRef.split("/")[1];
            let patient = patientId ? store.getState().patients?.[patientId] : null;
            if (!patient) {
                patient = await fetchPatientByReference(senderRef, fhirClient, store);
            }
            if (!patient || patient.resourceType !== "Patient") {
                console.log(`Invalid sender patient reference for comm ${comm.id}:`, senderRef);
                isValid = false;
            }
        }

        if (isValid) validated.push(comm);
    }

    //console.log(`Validated ${validated.length} out of ${messages.length} communications.`);
    //console.log("Validated communications:", validated);
    //let sanitized = sanitizeCommunications(validated);
    //console.log("Validated", validated);
    return validated;
    //return sanitized;
}

/**
 * UNUSED
 * Sanitize a single Communication resource by normalizing/removing "unknown" placeholders
 * and cleaning up invalid references so UI/filtering code can handle the resource.
 * @param {Object} comm
 * @returns {Object} sanitized communication
 */
export function sanitizeCommunication(comm) {

    if (!comm || typeof comm !== "object") return comm;
    // deep clone to avoid mutating original
    const c = JSON.parse(JSON.stringify(comm));

    const cleanRef = (ref) => {
        if (!ref) return null;
        if (typeof ref === "string" && ref.trim().toLowerCase() === "unknown") return null;
        return ref;
    };

    // Top-level reference fields
    if (c.sender?.reference) c.sender.reference = cleanRef(c.sender.reference);
    if (c.subject?.reference) c.subject.reference = cleanRef(c.subject.reference);
    if (c.encounter?.reference) c.encounter.reference = cleanRef(c.encounter.reference);

    // Recipients: normalize and drop invalid entries
    if (Array.isArray(c.recipient)) {
        c.recipient = c.recipient
            .map(r => ({ ...r, reference: cleanRef(r.reference) }))
            .filter(r => r && r.reference);
        if (c.recipient.length === 0) delete c.recipient;
    }

    // Priority/topic normalization
    if (typeof c.priority === "string" && c.priority.trim().toLowerCase() === "unknown") {
        delete c.priority;
    }

    if (typeof c.topic === "string" && c.topic.trim().toLowerCase() === "unknown") {
        delete c.topic;
    } else if (c.topic && typeof c.topic === "object") {
        const display = c.topic.coding?.[0]?.display;
        if (typeof display === "string" && display.trim().toLowerCase() === "unknown") {
            delete c.topic;
        }
    }

    // Sanitize extensions that are clearly placeholders (remove if they reference 'unknown')
    if (Array.isArray(c.extension)) {
        c.extension = c.extension.filter(ext => {
            if (!ext) return false;
            const ref = ext?.valueReference?.reference || ext?.reference;
            if (typeof ref === "string" && ref.trim().toLowerCase() === "unknown") return false;
            return true;
        });
        if (c.extension.length === 0) delete c.extension;
    }

    // Ensure strings that are the literal "unknown" are removed in common text fields
    if (typeof c.topic === "string" && c.topic.trim().toLowerCase() === "unknown") delete c.topic;
    if (typeof c.status === "string" && c.status.trim().toLowerCase() === "unknown") delete c.status;

    return c;
}

/**
 * Sanitize an array of Communication resources.
 * @param {Array} messages
 * @returns {Array} sanitized messages
 */
export function sanitizeCommunications(messages = []) {
    console.log("Sanitizing communications");
    console.log(messages);
    if (!Array.isArray(messages)) return messages;
    return messages.map(sanitizeCommunication);
}


//returns the (cached) display name of a referenceID (ie Patient/123)
export function resolveName(input, store) {
    //console.log("resolving name", input);
    let reference = input;

    // If it's an object, try to extract resourceType and id
    let resourceType, resourceId, cached;
    if (typeof input === "object" && input !== null) {
        resourceType = input.resourceType;
        resourceId = input.id;
        cached = input; // already hydrated
    } else if (typeof input === "string") {
        if (input.endsWith("/undefined")) return null;

        [resourceType, resourceId] = input.split("/");
        if (!resourceType || !resourceId) return null;

        const state = store?.getState?.();
        if (!state) return input;

        switch (resourceType) {
            case "Practitioner":
                cached = state?.providers?.[resourceId];
                break;
            case "Patient":
                cached = state?.patients?.[resourceId];
                break;
            case "Organization":
                cached = state?.organizations?.[resourceId];
                break;
            case "Encounter":
                cached = state?.encounters?.[resourceId];
                break;
            case "Observation":
                cached = state?.observations?.[resourceId];
                break;
            case "DiagnosticReport":
                cached = state?.diagnosticReports?.[resourceId];
                break;
            case "DocumentReference":
                cached = state?.documentReferences?.[resourceId];
                break;
            case "Specimen":
                cached = state?.specimens?.[resourceId];
                break;
            case "Communication":
                cached = state?.messages?.[resourceId];
                break;
            case "CareTeam":
                cached = state?.careTeams?.[resourceId];
                break;
            case "CarePlan":
                cached = state?.carePlans?.[resourceId];
                break;
            case "Condition":
                cached = state?.conditions?.[resourceId];
                break;
            case "Media":
                cached = state?.media?.[resourceId];
                break;
            case "ServiceRequest":
                cached = state?.serviceRequests?.[resourceId];
                break;
            case "QuestionnaireResponse":
                cached = state?.questionnaireResponses?.[resourceId];
                break;
            case "Goal":
                cached = state?.goals?.[resourceId];
                break;
            default:
                return input;
        }
    } else {
        return null;
    }

    if (!cached) return reference;

    // --- Practitioner / Patient ---
    if ((resourceType === "Practitioner" || resourceType === "Patient") && Array.isArray(cached.name) && cached.name.length > 0) {
        const entry = cached.name[0];
        const given = Array.isArray(entry.given) ? entry.given.join(" ") : entry.given || "";
        const family = entry.family || "";
        const text = entry.text || "";
        return text || `${given} ${family}`.trim() || reference;
    }

    // --- Organization ---
    if (resourceType === "Organization") {
        if (typeof cached.name === "string" && cached.name.trim()) return cached.name;
        if (Array.isArray(cached.name) && cached.name.length > 0) {
            const nameEntry = cached.name[0];
            if (typeof nameEntry === "string") return nameEntry;
            if (typeof nameEntry.text === "string") return nameEntry.text;
        }
        if (typeof cached.display === "string") return cached.display;
        if (Array.isArray(cached.identifier) && cached.identifier.length > 0) {
            const idEntry = cached.identifier[0];
            if (typeof idEntry.display === "string") return idEntry.display;
            if (typeof idEntry.value === "string") return idEntry.value;
        }
    }

    // --- Encounter ---
    if (resourceType === "Encounter") {
        // 1. type
        if (Array.isArray(cached.type) && cached.type.length > 0) {
            const typeEntry = cached.type[0];
            const display = typeEntry?.coding?.[0]?.display?.trim();
            //console.log(display);
            const text = typeEntry?.text?.trim();
            if (display) return display;
            if (text) return text;
        }
        // 2. class.display
        if (cached.class?.display) return cached.class.display.trim();

        // 3. serviceType
        const serviceDisplay = cached.serviceType?.coding?.[0]?.display?.trim() || cached.serviceType?.text?.trim();
        if (serviceDisplay) return serviceDisplay;

        // 4. reasonCode
        if (Array.isArray(cached.reasonCode) && cached.reasonCode.length > 0) {
            const r = cached.reasonCode[0];
            const display = r?.coding?.[0]?.display?.trim();
            const text = r?.text?.trim();
            if (display) return display;
            if (text) return text;
        }

        // 5. fallback to date
        if (cached.period?.start) return `Encounter on ${cached.period.start.substring(0, 10)}`;
        // 6. fallback literal
        return "Encounter";
    }

    // --- Observation ---
    if (resourceType === "Observation") {
        if (cached.code) {
            const codingDisplay = cached.code?.coding?.[0]?.display?.trim() || "";
            const text = cached.code?.text?.trim() || "";
            return codingDisplay || (text && text !== codingDisplay ? text : reference);
        }
    }

    // --- DiagnosticReport ---
    if (resourceType === "DiagnosticReport") {
        if (cached.code) {
            const codingDisplay = cached.code?.coding?.[0]?.display?.trim() || "";
            const text = cached.code?.text?.trim() || "";
            return codingDisplay || (text && text !== codingDisplay ? text : reference);
        }
    }

    // --- DocumentReference ---
    if (resourceType === "DocumentReference") {
        //console.log("Found doc reference...");
        const typeText = cached.type?.text?.trim() || cached.type?.coding?.[0]?.display?.trim() || "";
        const title = cached.title?.trim() || "";
        const description = cached.description?.trim() || "";
        return (
            typeText || title || (description && description !== title ? description : reference)
        );
    }

    // --- Specimen ---
    if (resourceType === "Specimen") {
        //console.log("Found doc reference...");
        const typeText = cached.type?.text?.trim() || cached.type?.coding?.[0]?.display?.trim() || "";
        const title = cached.title?.trim() || "";
        const description = cached.description?.trim() || "";
        return (
            typeText || title || (description && description !== title ? description : reference)
        );
    }

    if (resourceType === "Communication") {
        const commName = cached.topic?.coding?.[0]?.display?.trim() || cached.topic?.coding?.[0]?.code?.trim() || "";
        return commName;
    }

    // --- Media ---
    if (resourceType === "Media") {
        // Prefer content.title, then modality/type display, then created date, then id
        const contentTitle = cached.content?.title?.trim() || "";
        const modalityDisplay = cached.modality?.coding?.[0]?.display?.trim() || cached.modality?.text?.trim() || "";
        const typeDisplay = cached.type?.coding?.[0]?.display?.trim() || cached.type?.text?.trim() || "";
        const created = cached.createdDateTime ? `Media ${cached.createdDateTime.substring(0, 10)}` : "";

        return contentTitle || modalityDisplay || typeDisplay || created || cached.id || reference;
    }


    // --- CareTeam ---
    if (resourceType === "CareTeam") {
        const name =
            cached.name?.trim() ||

            // category.text OR first coding display/code
            cached.category?.[0]?.text?.trim() ||
            cached.category?.[0]?.coding?.[0]?.display?.trim() ||
            cached.category?.[0]?.coding?.[0]?.code?.trim() ||

            // final fallback
            cached.id ||
            reference;

        return name;
    }

    // --- CarePlan ---
    if (resourceType === "CarePlan") {
        const name =
            cached.title?.trim() ||
            cached.category?.[0]?.text?.trim() ||
            cached.category?.[0]?.coding?.[0]?.display?.trim() ||
            cached.category?.[0]?.coding?.[0]?.code?.trim() ||
            // final fallback
            cached.id ||
            reference;

        return name;
    }

    // --- Condition ---
    if (resourceType === "Condition") {
        // Prefer human-readable code text, then coding display, then clinical status/category, else id
        const codeText = cached.code?.text?.trim();
        const codingDisplay = cached.code?.coding?.[0]?.display?.trim();
        const clinical = cached.clinicalStatus?.coding?.[0]?.display?.trim() || cached.clinicalStatus?.text?.trim();
        const category = Array.isArray(cached.category) && cached.category.length > 0
            ? (cached.category[0]?.text?.trim() || cached.category[0]?.coding?.[0]?.display?.trim())
            : null;

        return codeText || codingDisplay || clinical || category || cached.id || reference;
    }

    // --- ServiceRequest ---
    if (resourceType === "ServiceRequest") {
        const codeText = cached.code?.text?.trim();
        const codingDisplay = cached.code?.coding?.[0]?.display?.trim();
        const categoryText = Array.isArray(cached.category) && cached.category.length > 0
            ? (cached.category[0]?.text?.trim() || cached.category[0]?.coding?.[0]?.display?.trim())
            : null;

        return codeText || codingDisplay || categoryText || cached.id || reference;
    }

    // --- QuestionnaireResponse ---
    if (resourceType === "QuestionnaireResponse") {
        // Try to get the questionnaire display or title
        const questionnaireRef = cached.questionnaire?.trim();
        const authored = cached.authored ? `Response ${cached.authored.substring(0, 10)}` : "";
        return questionnaireRef || authored || cached.id || reference;
    }

    // --- Goal ---
    if (resourceType === "Goal") {
        const description = cached.description?.text?.trim();
        const codingDisplay = cached.description?.coding?.[0]?.display?.trim();
        const lifecycleStatus = cached.lifecycleStatus?.trim();
        const name = description || codingDisplay || lifecycleStatus || cached.id || reference;
        return name.length > 50 ? name.substring(0, 75) + "..." : name;
    }

    return reference;
}





/**
 * Filters messages based on local flags (starred, snoozed, unread), priority, and payload content.
 *
 * @param {Array} messages - Array of Communication resources
 * @param {Object} options
 * @param {string} options.flag - "starred" | "snoozed" | "unread"
 * @param {Object} options.priorityFilter - e.g., { urgent: true, routine: true }
 * @param {string} options.practitionerId - Practitioner ID for flag matching
 * @param {string} [options.payloadQuery] - Optional search string to match payload content
 * @returns {Array} - Filtered message array
 */
export const filterLocalMessages = (
    messages = [],
    { flag = "none", practitionerId, payloadQuery } = {},
    messageById = {}
) => {
    const safeQuery = typeof payloadQuery === "string" ? payloadQuery.trim() : "";

    // Removed sanitization: use messages as provided
    const safeMessages = Array.isArray(messages) ? messages : [];

    // Helper now uses messageById override when available
    const hasStatusFlag = (comm, practitionerId, code) => {
        const m = comm?.id && messageById?.[comm.id] ? messageById[comm.id] : comm;
        if (!Array.isArray(m?.extension)) return false;
        return m.extension.some((ext) => {
            if (!ext || ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;
            const userRef = ext.extension?.find((e) => e.url === "user")?.valueReference?.reference;
            const flagCode = ext.extension?.find((e) => e.url === "flag")?.valueCode;
            const normalizedRef = userRef?.split?.("/")?.pop?.();
            return normalizedRef === practitionerId && flagCode === code;
        });
    };

    let filtered = safeMessages.filter((comm) => {
        const reasons = [];
        const isSnoozed = hasStatusFlag(comm, practitionerId, "snoozed");

        if (flag === "snoozed") {
            if (!isSnoozed) {
                reasons.push("not snoozed (snoozed view)");
            }
        } else {
            if (isSnoozed) {
                reasons.push("snoozed message excluded");
            }

            if (flag === "starred" && !hasStatusFlag(comm, practitionerId, "starred")) {
                reasons.push("not starred");
            }
            if (flag === "unread" && hasBeenReadByCurrentUser(comm, practitionerId)) {
                reasons.push("already read");
            }
        }

        if (reasons.length > 0) {
            // optional: keep debug logging
            // console.log(`❌ Message ${comm?.id || "<no-id>"} excluded:`, reasons);
            return false;
        }

        return true;
    });

    if (safeQuery) {
        filtered = filterMessagesByPayloadContent(filtered, safeQuery);
    }

    return filtered;
};
/**
 * Filters messages by payload content: contentString or attachment title
 *
 * @param {Array} messages - Array of Communication resources
 * @param {string} query - Case-insensitive search string
 * @returns {Array} - Filtered message array matching the payload text
 */
/**
 * Filters messages by payload content — contentString or attachment title.
 * Only applies filter when query has a non-empty, trimmed value.
 *
 * @param {Array} messages - Array of Communication resources
 * @param {string} query - Case-insensitive search string
 * @returns {Array} - Filtered message array, or original array if no query
 */
export const filterMessagesByPayloadContent = (messages = [], query = "") => {
    const safeQuery = typeof query === "string" ? query.trim().toLowerCase() : "";

    // Skip filtering if query is empty, zero-length, or not a string
    if (!safeQuery) return messages; // ✅ Only filter if there's actual input

    return messages.filter((comm) =>
        comm.payload?.some(
            (item) =>
                item.contentString?.toLowerCase().includes(safeQuery) ||
                item.contentAttachment?.title?.toLowerCase().includes(safeQuery)
        )
    );
};

// Helper to check for status flags (e.g. starred, snoozed)
const hasStatusFlag = (comm, practitionerId, code) => {
    return comm.extension?.some((ext) => {
        if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;
        const userRef = ext.extension?.find((e) => e.url === "user")?.valueReference?.reference;
        const flagCode = ext.extension?.find((e) => e.url === "flag")?.valueCode;
        const normalizedRef = userRef?.split("/")?.pop();
        return normalizedRef === practitionerId && flagCode === code;
    });
};


export const getStarredCount = (messages = [], practitionerId, byId = {}) => {
    // console.log("Counting starred messages:", messages);
    //console.log("Practitioner:", practitionerId);

    if (!practitionerId) {
        console.log("⭐ starredCount = 0 (no practitionerId)");
        return 0;
    }

    const starredCount = messages.filter(comm => {
        // Prefer authoritative Redux copy when available
        const msg = comm?.id && byId?.[comm.id] ? byId[comm.id] : comm;

        return msg.extension?.some(ext => {
            if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;

            const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
            const flagCode = ext.extension?.find(e => e.url === "flag")?.valueCode;
            const normalizedRef = userRef?.split("/")?.pop();

            return normalizedRef === practitionerId && flagCode === "starred";
        });
    }).length;

    //console.log("⭐ starredCount =", starredCount);
    return starredCount;
};


// utils/urgentCount.js

/**
 * Returns the number of Communication resources marked with priority "urgent".
 * 
 * @param {Array} messages - Array of FHIR Communication resources
 * @returns {number} - Count of urgent messages
 */
export const getUrgentCount = (messages = []) => {
    return messages.filter(comm =>
        comm.priority?.toLowerCase() === "urgent"
    ).length;
};


export const groupByDateRange = (messages) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfLastWeek = new Date(startOfToday);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

    const groups = {
        Today: [],
        Yesterday: [],
        "Last Week": [],
        Older: []
    };

    messages.forEach((msg) => {
        const sentDate = new Date(msg.sent);
        if (sentDate >= startOfToday) {
            groups.Today.push(msg);
        } else if (sentDate >= startOfYesterday) {
            groups.Yesterday.push(msg);
        } else if (sentDate >= startOfLastWeek) {
            groups["Last Week"].push(msg);
        } else {
            groups.Older.push(msg);
        }
    });

    // 🔽 Sort each group by descending sent date
    Object.keys(groups).forEach((group) => {
        groups[group].sort((a, b) => new Date(b.sent) - new Date(a.sent));
    });

    return groups;
};


// -------------------------------
// Recursive FHIR graph walker
// -------------------------------

/**
 * Recursively walks a FHIR resource graph and hydrates all references
 */
export function hydrateFhirGraph(input, fhirClient, store, { visited = new WeakSet() } = {}) {

    if (!input || typeof input !== "object") return;

    // Prevent infinite loops
    if (visited.has(input)) return;
    visited.add(input);

    /* ------------------------------
     * FHIR Reference Object
     * ------------------------------ */
    if (typeof input.reference === "string") {
        fetchByFhirReference(input.reference, fhirClient, store);
    }

    /* ------------------------------
     * Recurse all properties
     * ------------------------------ */
    for (const value of Object.values(input)) {
        if (Array.isArray(value)) {
            value.forEach(v =>
                hydrateFhirGraph(v, fhirClient, store, { visited })
            );
        } else if (typeof value === "object" && value !== null) {
            hydrateFhirGraph(value, fhirClient, store, { visited });
        }
    }
}



export function fetchByFhirReference(reference, fhirClient, store) {
    console.log("Fetching...", reference);
    if (!reference || typeof reference !== "string") return;

    const [resourceType] = reference.split("/");

    switch (resourceType) {
        case "Practitioner":
            return fetchProviderByReference(reference, fhirClient, store);

        case "Patient":
            return fetchPatientByReference(reference, fhirClient, store);

        case "Encounter":
            return fetchEncounterByReference(reference, fhirClient, store);

        case "Organization":
            return fetchOrganizationByReference(reference, fhirClient, store);

        case "Specimen":
            return fetchSpecimenByReference(reference, fhirClient, store);

        case "Observation":
            return fetchObservationByReference(reference, fhirClient, store);

        case "DiagnosticReport":
            return fetchDiagnosticReportByReference(reference, fhirClient, store);

        case "DocumentReference":
            return fetchDocumentReferenceByReference(reference, fhirClient, store);

        case "CareTeam":
            return fetchCareTeamByReference(reference, fhirClient, store);

        case "CarePlan":
            return fetchCarePlanByReference(reference, fhirClient, store);

        case "Condition":
            return fetchConditionByReference(reference, fhirClient, store);

        case "Media":
            return fetchMediaByReference(reference, fhirClient, store);

        case "ServiceRequest":
            return fetchServiceRequestByReference(reference, fhirClient, store);

        case "QuestionnaireResponse":
            return fetchQuestionnaireResponseByReference(reference, fhirClient, store);


        default:
            // Silently ignore unknown types (Device, Location, etc.)
            return;
    }
}







